// packages/web/src/lib/hooks/use-save-keyboard.ts
/**
 * useSaveKeyboard — global Ctrl+S / Cmd+S handler for manual sync flush.
 *
 * 19BN Phase 3: skips debounce — immediately drains the sync queue + sends
 * any pending course sync. Guarded by isTypingTarget so it doesn't hijack
 * the browser's native save in input fields when there's nothing to sync.
 *
 * Spec: docs/architecture/account-system-design.md §3.4, §9.3.4
 */

import { useEffect } from 'react';
import { useStore } from '../store/index.js';
import { isTypingTarget } from '../keyboard.js';

export function useSaveKeyboard(): void {
  const saveStatus = useStore((s) => s.saveStatus);
  const startSaving = useStore((s) => s.startSaving);
  const markSaved = useStore((s) => s.markSaved);
  const markError = useStore((s) => s.markError);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (!(e.ctrlKey || e.metaKey) || e.key !== 's') return;
      // In input fields, let the browser handle (or the field's own save).
      if (isTypingTarget(e)) return;
      e.preventDefault();
      // Only flush if there's something pending.
      if (saveStatus !== 'pending') return;
      startSaving();
      // The actual sync is triggered by the sync engine listener.
      // markSaved/markError will be called by the sync engine. Safety net: if
      // the engine hasn't resolved within 3s, mark error so the indicator
      // doesn't hang on "saving" forever (engine crash, IDB lock, etc.).
      setTimeout(() => {
        const current = useStore.getState().saveStatus;
        if (current === 'saving') {
          useStore.getState().markError();
        }
      }, 3000);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [saveStatus, startSaving, markSaved, markError]);
}
